import { Component, Input} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { passageStyle } from '../../constants/passage-constants';
import { UtilityService } from 'src/app/shared/services/utility.service';

@Component({
    selector: 'passage-text',
    templateUrl: './passage-text.component.html',
    styleUrls: ["./passage-text.component.css"],
    standalone: false
})
export class LessonTextComponent {
  private _paragraph: string = '';
  renderedParagraph: SafeHtml;
  rawHtml:string
  @Input() 
  set paragraph(value: string) {
    this._paragraph = value;
    // this.processParagraph();
  }
  get paragraph(): string {
    return this._paragraph;
  }
  @Input() secondHearing: boolean = false;
  @Input() firstHearing: boolean = false;
  @Input() isSavingLoader: boolean = false;

  words: Word[] = [];
  private _firstReadingWords: string[] = [];

  @Input() 
  set firstReadingWords(value: string[]) {
    this._firstReadingWords = value;
    // this.processParagraph();
  }
  get firstReadingWords(): string[] {
    return this._firstReadingWords;
  }
  private _firstReadingWordsIndex: number[] = [];

  @Input() 
  set firstReadingWordsIndex(value: number[]) {
    this._firstReadingWordsIndex = value;
    // this.processParagraph();
  }
  get firstReadingWordsIndex(): number[] {
    return this._firstReadingWordsIndex;
  }
  private _secondReadingWordsIndex: number[] = [];

  @Input() 
  set secondReadingWordsIndex(value: number[]) {
    this._secondReadingWordsIndex = value;
    this.processParagraph();
  }
  get secondReadingWordsIndex(): number[] {
    return this._secondReadingWordsIndex;
  }
  @Input() secondReadingWords :string[];
  @Input() whipWords :string[];
  constructor(private sanitizer: DomSanitizer) {}

  processParagraph() {
    const tempDiv = document.createElement('article');
    tempDiv.innerHTML = this._paragraph; // 'this._paragraph' is HTML
  
    const traverseNode = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Clean up unnecessary newlines to avoid double <br>
        const text = node.textContent?.replace(/\n+/g, ' ') || '';
        const wordRegex = UtilityService.createPassageWordRegex();
        let match;
        let result = '';
        let lastIndex = 0;
    
        while ((match = wordRegex.exec(text)) !== null) {
          // Add non-word filler
          if (match.index > lastIndex) {
            result += text.substring(lastIndex, match.index);
          }
    
          const word = match[0];
          const wordIndex = this.words.length;
          this.addWord(word);
    
          result += `<span class="word" data-index="${wordIndex}">${word}</span>`;
          lastIndex = match.index + word.length;
        }
    
        // Add remaining filler
        if (lastIndex < text.length) {
          result += text.substring(lastIndex);
        }
    
        return result;
      }
    
      else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
    
        // Handle <br> explicitly
        if (element.tagName.toLowerCase() === 'br') {
          return '<br>';
        }
    
        // Traverse children
        const childrenHTML = Array.from(element.childNodes).map(traverseNode).join('');
    
        // Preserve tag and its attributes
        const tagName = element.tagName.toLowerCase();
        const attributes = Array.from(element.attributes)
          .map(attr => `${attr.name}="${attr.value}"`)
          .join(' ');
    
        return `<${tagName}${attributes ? ' ' + attributes : ''}>${childrenHTML}</${tagName}>`;
      }
    
      return '';
    };
  
    this.words = [];
    const processedHTML = traverseNode(tempDiv);
    this.rawHtml = processedHTML;
    // this.renderedParagraph = processedHTML; // Store the processed HTML
    this.renderedParagraph = this.sanitizer.bypassSecurityTrustHtml(processedHTML);
    this.updateWordStyles(true)
  }
  handleWordClick(event: MouseEvent) {
    const target = (event.target as HTMLElement).closest('.word') as HTMLElement; 
    if (target && target.dataset.index) {
      const index = parseInt(target.dataset.index, 10);
      if (!isNaN(index) && !this.isSavingLoader) {
        this.toggleMarking(index);
        this.updateWordStyles(false,target); // Update styles after toggling
      }
    }
  }
  private setInitialWordMarking(word: Word, index: number): void {
    word.highlighted = this._firstReadingWordsIndex.includes(index) &&
                       this._firstReadingWords.includes(word.text);
  
    word.underlined = this._secondReadingWordsIndex.includes(index) &&
                      this.secondReadingWords.includes(word.text);
  }
  private applyStylesToSpan(span: HTMLElement, word: Word): void {
    span.style.backgroundColor = word.highlighted
      ? passageStyle.HIGHLIGHT.backgroundColor
      : passageStyle.NO_HIGHLIGHT.backgroundColor;
  
    if (word.underlined) {
      span.style.textDecoration = passageStyle.UNDERLINE.textDecoration;
      span.style.textDecorationColor = passageStyle.UNDERLINE.textDecorationColor;
      span.style.textDecorationThickness = passageStyle.UNDERLINE.textDecorationThickness;
    } else {
      span.style.textDecoration = passageStyle.NO_UNDERLINE.textDecoration;
    }
  }
  updateWordStyles(initialRendering: boolean = false, target?: HTMLElement) {

    if (!initialRendering && target) {
      const index = parseInt(target.dataset.index!, 10);
      const word = this.words[index];
      this.applyStylesToSpan(target, word);
      return;
    }
  
    const tempDiv = document.createElement('article');
    tempDiv.innerHTML = this.rawHtml;
  
    const wordSpans = tempDiv.querySelectorAll('.word');
    for (let index = 0; index < wordSpans?.length; index++) {
      const span = wordSpans[index] as HTMLElement;
      const word = this.words[index];
  
      if (initialRendering) {
        this.setInitialWordMarking(word, index);
      }
  
      this.applyStylesToSpan(span, word);
    }
  
    this.rawHtml = tempDiv.innerHTML;
    this.renderedParagraph = this.sanitizer.bypassSecurityTrustHtml(this.rawHtml);
  }
  
  addWord(word: string) {
    this.words.push(new Word(word, false, false, true));
  }
  addFiller(filler: string) {
    this.words.push(new Word(filler, false, false, false));
  }

  toggleMarking(index: number) {
    if(this.secondHearing){
      this.toggleUnderline(index)
    }else if (this.firstHearing){
      this.toggleHighlight(index)
    }
  }
  isWordAlreadyExist(word: string, existingReadingWords: string[]): boolean {
    const lowercaseWord = word.toLowerCase();
    const lowercaseSet = new Set(existingReadingWords?.map(str => str.toLowerCase()));
    return lowercaseSet.has(lowercaseWord);
  }
  getWhipWords() {
  const secondReadingWordsSet = new Set(this.secondReadingWords);
  const commonWords = this.firstReadingWords.filter(item => secondReadingWordsSet.has(item));
  // Use a Set to remove duplicates
  const uniqueCommonWords = Array.from(new Set(commonWords));

  this.whipWords.length = 0; // clear existing array
  this.whipWords.push(...uniqueCommonWords);
  }
  toggleHighlight(index: number) {
    if(this.words[index].can_toggle &&!this.isWordAlreadyExist(this.words[index].text, this.firstReadingWords)) {
      this.words[index].highlighted = true;
        this.firstReadingWords.push(this.words[index].text);
        this.firstReadingWordsIndex.push(index);
    
    } else{
      const wordIndex = this.firstReadingWords.indexOf(this.words[index].text);
      const firstReadingWordIndex = this.firstReadingWordsIndex.indexOf(index);
      if (wordIndex > -1 && wordIndex === firstReadingWordIndex ) {
        this.words[index].highlighted = false;
        this.firstReadingWords.splice(wordIndex, 1);
        this.firstReadingWordsIndex.splice(firstReadingWordIndex, 1);
      }
    }
    this.getWhipWords()
  }

  toggleUnderline(index: number) {
    if(this.words[index].can_toggle && !this.isWordAlreadyExist(this.words[index].text, this.secondReadingWords)) {
      this.words[index].underlined = true;
        this.secondReadingWords.push(this.words[index].text);
        this.secondReadingWordsIndex.push(index);
    }else{
      const wordIndex = this.secondReadingWords.indexOf(this.words[index].text);
      const secondReadingWordIndex = this.secondReadingWordsIndex.indexOf(index);

      if (wordIndex > -1 && wordIndex === secondReadingWordIndex) {
        this.words[index].underlined = false;
        this.secondReadingWords.splice(wordIndex, 1);
        this.secondReadingWordsIndex.splice(secondReadingWordIndex, 1);
      }
    }
    this.getWhipWords();
  }
}


class Word {
  text: string;
  highlighted: boolean;
  underlined: boolean;
  can_toggle: boolean;
  constructor(text: string, highlighted: boolean, underlined: boolean, can_toggle: boolean) {
    this.text = text;
    this.highlighted = highlighted;
    this.underlined = underlined;
    this.can_toggle = can_toggle;
  }
  appendText(text: string) {
    this.text += text;
  }
}
